import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { FiltersValue } from '../types/filters';

const isDevelopment = process.env.NODE_ENV === 'development';

interface Option { id: string; name: string; }

interface FiltersContextType {
  filters: FiltersValue;
  setFilters: (filters: FiltersValue) => void;
  updateFilter: <K extends keyof FiltersValue>(key: K, value: FiltersValue[K]) => void;
  // Shared options to avoid redundant DuckDB queries
  options: {
    kommuner: Option[];
    skolor: Option[];
    kpis: Option[];
    years: number[];
    loading: boolean;
  };
  setOptions: (options: Partial<FiltersContextType['options']>) => void;
  // Legacy compatibility
  value: FiltersValue;
  setValue: (filters: FiltersValue) => void;
}

const FiltersContext = createContext<FiltersContextType | undefined>(undefined);

interface FiltersProviderProps {
  children: ReactNode;
}

export function FiltersProvider({ children }: FiltersProviderProps) {
  // Initialize state from localStorage if available, otherwise defaults
  const [filters, setFiltersState] = useState<FiltersValue>(() => {
    // ... same defaults ...
    const defaults: FiltersValue = {
      kommunId: null,
      huvudmanId: null,
      skolaId: null,
      year: 2024,
      kpiId: null,
      compareScope: 'municipality',
      activeTab: null,
      governance: '',
      analyticalFilters: {}
    };

    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('kolada_filters');
        if (saved) {
          const parsed = JSON.parse(saved);
          return { ...defaults, ...parsed };
        }
      } catch (e) {
        console.error('Failed to parse saved filters', e);
      }
    }
    return defaults;
  });

  const [options, setOptionsState] = useState<FiltersContextType['options']>({
    kommuner: [],
    skolor: [],
    kpis: [],
    years: [],
    loading: false
  });

  const setOptions = useCallback((newOptions: Partial<FiltersContextType['options']>) => {
    setOptionsState(prev => ({ ...prev, ...newOptions }));
  }, []);

  // Load filters from URL on mount (overriding localStorage)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlFilters: Partial<FiltersValue> = {};

    // Debug logging
    if (isDevelopment) console.log('Loading filters from URL:', {
      kommun: urlParams.get('kommun'),
      huvudman: urlParams.get('huvudman'),
      governance: urlParams.get('governance'),
      skola: urlParams.get('skola'),
      year: urlParams.get('year')
    });

    if (urlParams.get('kommun')) urlFilters.kommunId = urlParams.get('kommun');
    if (urlParams.get('huvudman')) urlFilters.huvudmanId = urlParams.get('huvudman');
    if (urlParams.get('skola')) urlFilters.skolaId = urlParams.get('skola');
    if (urlParams.get('year')) urlFilters.year = parseInt(urlParams.get('year') || '2024');
    if (urlParams.get('kpi')) urlFilters.kpiId = urlParams.get('kpi');
    if (urlParams.get('tab')) urlFilters.activeTab = urlParams.get('tab');
    if (urlParams.get('scope')) {
      const scope = urlParams.get('scope');
      if (scope === 'national' || scope === 'salsa') {
        urlFilters.compareScope = scope;
      } else {
        urlFilters.compareScope = 'municipality';
      }
    }

    // Backward compatibility: migrate old 'governance' parameter to 'huvudmanId'
    if (urlParams.get('governance') && !urlParams.get('huvudman')) {
      const governance = urlParams.get('governance');
      if (governance === 'kommunal' || governance === 'fristående') {
        urlFilters.huvudmanId = governance;
        urlFilters.governance = governance; // Keep for backward compatibility
        if (isDevelopment) console.log('Migrated governance to huvudmanId:', governance);
      }
    } else if (urlParams.get('governance')) {
      // If both exist, prefer 'huvudman' but keep 'governance' for compatibility
      const governance = urlParams.get('governance');
      urlFilters.governance = (governance === 'kommunal' || governance === 'fristående') ? governance : '';
    }

    // Parse analytical filters from URL (comma-separated)
    if (urlParams.get('analytical')) {
      const analyticalParam = urlParams.get('analytical');
      const analyticalFlags = analyticalParam?.split(',') || [];
      urlFilters.analyticalFilters = {
        underExpected: analyticalFlags.includes('underExpected'),
        overExpected: analyticalFlags.includes('overExpected'),
        outliers: analyticalFlags.includes('outliers'),
        topImprovement: analyticalFlags.includes('topImprovement')
      };
    }

    if (Object.keys(urlFilters).length > 0) {
      if (isDevelopment) console.log('Setting filters from URL (overriding localStorage):', urlFilters);
      setFiltersState(prev => ({ ...prev, ...urlFilters }));
    }
  }, []);

  // Update URL AND localStorage when filters change
  useEffect(() => {
    // 1. Save to LocalStorage
    try {
      localStorage.setItem('kolada_filters', JSON.stringify(filters));
    } catch (e) {
      console.error('Failed to save filters to localStorage', e);
    }

    // 2. Update URL
    const url = new URL(window.location.href);

    // Debug logging
    if (isDevelopment) console.log('Updating URL with filters:', {
      kommunId: filters.kommunId,
      huvudmanId: filters.huvudmanId,
      skolaId: filters.skolaId,
      year: filters.year
    });

    if (filters.kommunId) url.searchParams.set('kommun', filters.kommunId);
    else url.searchParams.delete('kommun');

    if (filters.huvudmanId) {
      url.searchParams.set('huvudman', filters.huvudmanId);
      // Also set governance for backward compatibility
      url.searchParams.set('governance', filters.huvudmanId);
    } else {
      url.searchParams.delete('huvudman');
      // Only delete governance if it's not set separately
      if (!filters.governance) {
        url.searchParams.delete('governance');
      }
    }

    if (filters.skolaId) url.searchParams.set('skola', filters.skolaId);
    else url.searchParams.delete('skola');

    if (filters.year) url.searchParams.set('year', filters.year.toString());
    else url.searchParams.delete('year');

    if (filters.kpiId) url.searchParams.set('kpi', filters.kpiId);
    else url.searchParams.delete('kpi');

    if (filters.activeTab) url.searchParams.set('tab', filters.activeTab);
    else url.searchParams.delete('tab');

    if (filters.compareScope !== 'municipality') url.searchParams.set('scope', filters.compareScope);
    else url.searchParams.delete('scope');

    if (filters.governance) url.searchParams.set('governance', filters.governance);
    else url.searchParams.delete('governance');

    // Persist analytical filters to URL (comma-separated)
    if (filters.analyticalFilters) {
      const activeAnalytical = Object.entries(filters.analyticalFilters)
        .filter(([_, value]) => value === true)
        .map(([key]) => key);

      if (activeAnalytical.length > 0) {
        url.searchParams.set('analytical', activeAnalytical.join(','));
      } else {
        url.searchParams.delete('analytical');
      }
    } else {
      url.searchParams.delete('analytical');
    }

    window.history.replaceState({}, '', url.toString());
  }, [filters]);

  const setFilters = useCallback((newFilters: FiltersValue) => {
    setFiltersState(prev => {
      // Apply dependency logic when setting multiple filters
      const processedFilters = { ...newFilters };

      // If kommunId is being changed, clear dependent filters
      if (newFilters.kommunId !== prev.kommunId) {
        processedFilters.huvudmanId = null;
        processedFilters.skolaId = null;
        if (isDevelopment) console.log('Kommun changed via setFilters, clearing huvudman and skola');
      }
      // If huvudmanId is being changed, clear skola
      else if (newFilters.huvudmanId !== prev.huvudmanId) {
        processedFilters.skolaId = null;
        if (isDevelopment) console.log('Huvudman changed via setFilters, clearing skola');
      }

      return processedFilters;
    });
  }, []);

  const updateFilter = useCallback(<K extends keyof FiltersValue>(key: K, value: FiltersValue[K]) => {
    setFiltersState(prev => {
      const newFilters = { ...prev, [key]: value };

      // Dependency logic: clear dependent filters when parent filters change
      if (key === 'kommunId') {
        // When kommun changes, clear huvudman and skola
        newFilters.huvudmanId = null;
        newFilters.skolaId = null;
        if (isDevelopment) console.log('Kommun changed, clearing huvudman and skola');
      } else if (key === 'huvudmanId') {
        // When huvudman changes, clear skola
        newFilters.skolaId = null;
        if (isDevelopment) console.log('Huvudman changed, clearing skola');
      }

      return newFilters;
    });
  }, []);

  const contextValue = useMemo<FiltersContextType>(() => ({
    filters,
    setFilters,
    updateFilter,
    options,
    setOptions,
    // Legacy compatibility
    value: filters,
    setValue: setFilters
  }), [filters, setFilters, updateFilter, options, setOptions]);

  return (
    <FiltersContext.Provider value={contextValue}>
      {children}
    </FiltersContext.Provider>
  );
}

export function useFilters() {
  const context = useContext(FiltersContext);
  if (context === undefined) {
    throw new Error('useFilters must be used within a FiltersProvider');
  }
  return context;
}